import React from 'react';
import cn from 'classnames';
import nl2br from 'react-nl2br';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import { ILink } from 'tbk-components/src/components/Button';

export interface IAlternatingItemProps {
	/**
	 * Alternating Item Title
	 */
	title?: string;
	/**
	 * Alternating Item Text
	 * @control richtext
	 */
	text?: string;
	/**
	 * Alternating Item Image - Image should be 523px X 392px
	 */
	image?: IImage;
	/**
	 * Automatically crops the image to match the height of the written content.
	 */
	autoFitImage?: boolean;
	/**
	 * Alternating Item Featured Link
	 */
	link?: ILink;
}

export interface IAlternatingTextAndImagesProps extends IBaseElementProps {
	alternatingItems?: IAlternatingItemProps[];
}

/**
 * Alternating Text and Images
 * @block
 * @requiresHooks
 * @icon id
 */
const AlternatingTextAndImages: React.FC<IAlternatingTextAndImagesProps> = ({
	alternatingItems = [],
	className,
	classNames = [],
}) => (
	<div
		className={
			className ||
			cn('alternating-text-and-images', 'container', ...classNames)
		}
	>
		<div className="alternating-text-and-images__container container-fluid">
			{alternatingItems.length > 0 && (
				<>
					{alternatingItems.map((item, index) => (
						<div key={index} className="image-and-content row">
							{item?.image?.src && (
								<script type="application/ld+json">
									{`
										{
											"@context": "http://schema.org/",
											"@type": "Article",
											"description": "${item?.text ?? ''}",
											"image": "${item.image.src}",
											"name": "${item?.title ?? ''}"
										}
									`}
								</script>
							)}
							{item.image && (
								<div className="image-and-content__image col-12 col-lg-6">
									<div className="image-wrapper">
										<Image {...item.image} className={item.autoFitImage && 'img-fluid'} />
									</div>
								</div>
							)}
							<div className="image-and-content__content col-12 col-lg-6">
								<div className="image-and-content__content-inner">
									{item.title && <h2>{item.title}</h2>}
									{item.text && (
										<div
											className="image-and-content__content-text"
											dangerouslySetInnerHTML={{
												__html: item.text,
											}}
										/>
									)}
									{item.link && (
										<a
											className="btn btn-primary image-and-content__link"
											href={item.link.href}
											target={item.link.target}
										>
											{item.link.label}
										</a>
									)}
								</div>
							</div>
						</div>
					))}
				</>
			)}
		</div>
	</div>
);

export default AlternatingTextAndImages;
